/**
 * Helper: Wishlist
 * ------------------------------------------------------------------------------
 * - Helper file for interacting with Swym Wishlist/Watchlist.
 * - Uses event listeners to open a Vue-based BIS modal, which leverages the API.
 * - This file exists as long as the site uses the legacy variant selectors (shop-the-look, grid-variant-option-selector)
 * - Aim to gradually move towards 100% API usage and phase out this helper.
 * - https://api-docs.swym.it/v3/index.html#introduction-to-v3-0
 *
 * @namespace Wishlist
 */
import { waitFor } from '~/helpers/dom'

// window.SwymCallbacks = window.SwymCallbacks || []

/**
 * Create a new Wishlist helper.
 */
export default () => {

  /**
   * Initialise component.
   */
  function init() {
    mint.EventBus.on('swym:ready', () => {
      setDefaultState()
    })
  }

  /**
   * Set the default state.
   */
  function setDefaultState() {
    setEventBusListeners()

    mint.EventBus.dispatch('wishlist:ready')
  }

  /**
   * Set eventBus Listeners
   */
  function setEventBusListeners() {

    /**
     * Initiate actions to open the 'Notify me' back in stock modal.
     * - ES6 components can use the EventBus, as below
     * - Vue components should use Pinia, which will also emit a mock event.
     * @example
     *  mint.EventBus.dispatch('wishlist:addToWatchlist', {
     *    product: <Product Object>,
     *    variant: <Variant Object>,
     *  });
     */
    mint.EventBus.on('wishlist:addToWatchlist', (payload) => {
      handleAddToWatchlist(payload)
    })
  }

  /**
   * Trigger the 'Back In Stock' alert modal.
   * @param {Object} product - Shopify product object.
   * @param {Object} variant - Shopify variant object.
   * @param {Event} event - Click event, required for Swym.
   * @param {String} sizeValue - International equivalent size value for the current variant.
   *
   * @fires product:openBackInStock
   */
  function handleAddToWatchlist({ product, variant, event, sizeValue }) {
    if (!product || !variant) {
      mint.Log(
        'error',
        'Back In Stock triggered without variant or product',
        new Error('Back In Stock triggered without variant or product'),
        { product, variant },
      )

      return
    }

    let handle = false

    if (product.url) {
      handle = product.url.split('products/')[1].split('?')[0]
    }

    if (product.handle) {
      handle = product.handle
    }

    if (!handle) {
      mint.Log(
        'error',
        'Triggering Back In Stock failed: No product handle',
        new Error('Triggering Back In Stock failed: No product handle'),
        {
          location: 'Shop The Look',
          url: location.url,
          product,
        })

      return
    }

    /**
     * International Sizes.
     */
    let variantTitle = variant.title

    /**
     * Variant Title is output to the front-end, the value will be:
     * 'Option Name' or 'Option 1 / Option 2', e.g.
     * 'Regular / 10'; '08'; 'SMALL'; 'Long / MEDIUM'.
     * - Would prefer to use Liquid's `options_with_values` here, but
     *   Swym doesn't return these values, so
     *   use this method when using both API response and Shopify object.
     * - Regex check for forward-slash surrounded by two spaces (' / ')
     */
    if (sizeValue) {
      const variantArray = variantTitle.split('/')

      let titleSizeValue = variantArray[1] || variantArray[0]
      titleSizeValue = titleSizeValue.trim()

      variantTitle = variantTitle.replace(
        titleSizeValue,
        sizeValue,
      )
    }

    variant.title = variantTitle

    mint.EventBus.dispatch('product:openBackInStock', {
      event,
      product,
      variant,
    })
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
